import React from 'react';
import PropTypes from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowPointer,
  faBan,
  faEnvelopeCircleCheck,
  faEye,
  faPaperPlane,
  faTriangleExclamation
} from '@fortawesome/pro-solid-svg-icons';

import { Modal } from '@buildoutinc/blueprint-react/ui/Modal';

import { Alert } from 'components/ui/alert';
import { Button } from 'components/ui/button';
import InviteStatusPill from 'components/documents/invite_status_pill';
import { showSnackbar } from 'components/ui/snackbar/helper';

// One entry per failure the broker can actually act on. The CTAs are the point:
// a hard bounce needs the address fixed, a block needs the broker to send it
// themselves, a soft bounce just needs another try.
const FAILURES = {
  hard_bounce: {
    title: "This address doesn't exist. Check it for typos.",
    retry: false,
    copyLink: false,
    editEmail: true
  },
  soft_bounce: {
    title: 'Temporarily undeliverable — the mailbox is full or the server was unavailable.',
    detail: 'SendGrid stopped retrying. Sending again is usually enough.',
    retry: true,
    copyLink: true,
    editEmail: false
  },
  blocked: {
    title: "Rejected by the recipient's mail server",
    detail: 'Check the address for typos, or copy the invite link and send it from your own inbox.',
    retry: false,
    copyLink: true,
    editEmail: true
  },
  suppressed: {
    title: 'Not sent — this address is on our suppression list from an earlier bounce.',
    detail: 'Copy the invite link and send it from your own inbox.',
    retry: false,
    copyLink: true,
    editEmail: false
  },
  // The mail arrived and the recipient reported it, so every "send it another
  // way" action here would make things worse. Deliberately no CTAs.
  spam_reported: {
    title: 'The recipient reported this email as spam.',
    detail: 'Do not resend. Reach out another way if you still need to contact them.',
    retry: false,
    copyLink: false,
    editEmail: false
  },
  not_sent: {
    title: "We couldn't send this invite.",
    detail: 'Something went wrong on our end and support has been notified. Trying again usually works.',
    retry: true,
    copyLink: false,
    editEmail: false
  }
};

// SendGrid dropped these (or we failed before handing off), so nothing ever left
// the building. Showing "Sent" for them would contradict the failure alert.
const NEVER_SENT_STATUSES = ['suppressed', 'not_sent'];

function formatTimestamp(value) {
  if (!value) return '';

  return new Date(value).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  });
}

export function Stage({ icon, label, detail, timestamp }) {
  return (
    <div className="d-flex justify-content-between align-items-start mb-3">
      <div className="d-flex gap-2">
        <FontAwesomeIcon className="mt-1 text-muted" icon={icon} />
        <div>
          <div>{label}</div>
          {detail ? <div className="text-muted small">{detail}</div> : null}
        </div>
      </div>
      <div className="text-muted small text-nowrap">{formatTimestamp(timestamp)}</div>
    </div>
  );
}

Stage.propTypes = {
  detail: PropTypes.string,
  icon: PropTypes.object.isRequired,
  label: PropTypes.string.isRequired,
  timestamp: PropTypes.string
};

Stage.defaultProps = { detail: null, timestamp: null };

export function InviteActivityBody({ activity, shareLink, onRetry, onEditEmail }) {
  const failure = activity.failureType ? FAILURES[activity.failureType] : null;

  const copyLink = () => {
    navigator.clipboard.writeText(shareLink.url);
    showSnackbar({ text: 'Invite link copied', variant: 'success' });
  };

  const neverSent = NEVER_SENT_STATUSES.includes(activity.status);

  return (
    <React.Fragment>
      {neverSent ? (
        <Stage
          detail={activity.sentBy ? `Attempted by ${activity.sentBy}` : null}
          icon={faBan}
          label="Not sent"
          timestamp={activity.sentAt}
        />
      ) : (
        <Stage
          detail={activity.sentBy ? `by ${activity.sentBy}` : null}
          icon={faPaperPlane}
          label="Sent"
          timestamp={activity.sentAt}
        />
      )}

      {activity.deliveredAt ? (
        <Stage
          detail="Accepted by recipient server"
          icon={faEnvelopeCircleCheck}
          label="Delivered"
          timestamp={activity.deliveredAt}
        />
      ) : null}

      {activity.opens > 0 ? (
        <Stage
          detail={activity.opens === 1 ? 'once' : `${activity.opens} times`}
          icon={faEye}
          label="Opened"
          timestamp={activity.lastOpenedAt}
        />
      ) : null}

      {activity.clicks > 0 ? (
        <Stage
          detail={activity.signedCa ? 'CA signed' : 'CA not yet signed'}
          icon={faArrowPointer}
          label="Clicked invite link"
          timestamp={activity.lastClickedAt}
        />
      ) : null}

      {failure ? (
        <Alert icon={faTriangleExclamation} severity="destructive">
          <Alert.Title>{failure.title}</Alert.Title>
          {failure.detail ? <div className="small">{failure.detail}</div> : null}
          <div className="d-flex gap-2 mt-2">
            {failure.retry ? <Button label="Try again" onClick={onRetry} /> : null}
            {failure.copyLink && shareLink ? (
              <Button label="Copy invite link" variant="secondary" onClick={copyLink} />
            ) : null}
            {failure.editEmail ? (
              <Button label="Edit email address" variant="secondary" onClick={onEditEmail} />
            ) : null}
          </div>
        </Alert>
      ) : null}
    </React.Fragment>
  );
}

InviteActivityBody.propTypes = {
  activity: PropTypes.object.isRequired,
  shareLink: PropTypes.object,
  onEditEmail: PropTypes.func.isRequired,
  onRetry: PropTypes.func.isRequired
};

InviteActivityBody.defaultProps = { shareLink: null };

export default function InviteActivityModal({ show, lead, onHide, onRetry, onEditEmail }) {
  const activity = lead.inviteActivity;
  if (!activity) return null;

  return (
    <Modal open={show} onClose={onHide}>
      <Modal.Portal>
        <Modal.Content>
          <Modal.Header>
            <div className="d-flex justify-content-between align-items-center w-100">
              <div>
                <Modal.Title>Invite activity</Modal.Title>
                <div className="text-muted small">{activity.email}</div>
              </div>
              <InviteStatusPill status={activity.status} />
            </div>
          </Modal.Header>

          <Modal.Body>
            <InviteActivityBody
              activity={activity}
              shareLink={(lead.shareLinks || [])[0] || null}
              onEditEmail={onEditEmail}
              onRetry={onRetry}
            />
          </Modal.Body>
        </Modal.Content>
      </Modal.Portal>
    </Modal>
  );
}

InviteActivityModal.propTypes = {
  lead: PropTypes.object.isRequired,
  show: PropTypes.bool.isRequired,
  onEditEmail: PropTypes.func,
  onHide: PropTypes.func.isRequired,
  onRetry: PropTypes.func.isRequired
};

InviteActivityModal.defaultProps = { onEditEmail: () => {} };
